import type { BUITheme } from '@blofin/blofin-ui';
import { Pagination } from '@blofin/blofin-ui';
import PageLayout from 'components/layout/PageLayout';
import Breadcrumb from 'components/common/Breadcrumb';
import ArticleListItem from 'components/shared/ArticleListItem';
import ListPageHeader from 'components/shared/ListPageHeader';
import useLocaleLink from 'hooks/useLocaleLink';
import type { GetServerSideProps } from 'next';
import { useTranslations } from 'next-intl';
import Head from 'next/head';

import { useCallback } from 'react';
import { getArticleList } from 'services/help-center';
import { articleSummaryToListItem } from 'services/help-center/utils';
import { requireMessages } from 'services/http/requireMessages';
import type { ArticleListItem as ArticleListItemType } from 'types/help-center';
import BlofinNative from 'sdk';

const PAGE_SIZE = 10;

interface TermsListPageProps {
  theme: BUITheme;
  articles: ArticleListItemType[];
  page: number;
  totalPages: number;
  messages: Record<string, unknown>;
  isApp: boolean;
}

export const getServerSideProps: GetServerSideProps<TermsListPageProps> = async (ctx) => {
  const cookies = ctx.req.headers.cookie || '';
  const themeMatch = cookies.match(/(?:^|;\s*)theme=(light|dark)/);
  const theme = (themeMatch?.[1] as BUITheme) || 'light';
  const isApp = BlofinNative.isApp(ctx.req.headers['user-agent'] || '');

  const page = Math.max(1, parseInt(ctx.query.page as string, 10) || 1);
  const language = ctx.locale || 'en';

  let articles: ArticleListItemType[] = [];
  let totalPages = 0;
  let messages: Record<string, unknown> = {};

  try {
    const [listResult, messagesResult] = await Promise.allSettled([
      getArticleList({ language, categoryCode: 'terms', page, pageSize: PAGE_SIZE }),
      requireMessages(['common', 'components', 'support'], ctx.locale),
    ]);

    if (messagesResult.status === 'fulfilled') {
      messages = messagesResult.value;
    }

    if (listResult.status === 'fulfilled' && listResult.value?.code === 200) {
      const pageResult = listResult.value?.data;
      articles = (pageResult?.items ?? []).map((a) => articleSummaryToListItem(a));
      totalPages = Math.ceil((pageResult?.total ?? 0) / PAGE_SIZE);
    }
  } catch (error) {
    console.log('[TermsList] getServerSideProps error:', error);
  }

  return { props: { theme, articles, page, totalPages, messages, isApp } };
};

export default function TermsListPage({ theme, articles, page, totalPages, isApp }: TermsListPageProps) {
  const t = useTranslations('support');
  const localeLink = useLocaleLink();

  const handlePageChange = useCallback(
    (newPage: number) => {
      window.location.href = `${localeLink}/support/Terms?page=${newPage}`;
    },
    [localeLink]
  );

  return (
    <PageLayout theme={theme} showTheme hideHeader={isApp} hideFooter={isApp}>
      <Head>
        <title>{t('seoTermsPageTitle')}</title>
        <meta name="description" content={t('seoTermsPageDesc')} />
      </Head>
      <section className="-mt-16 w-full bg-light-background px-4 pb-10 pt-[88px] dark:bg-base-fill-primary pad:px-8 pad:pb-20 pad:pt-[104px]">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 pad:gap-10">
          <Breadcrumb items={[{ label: t('helpCenter'), href: '/support' }, { label: t('termsTitle') }]} />

          <ListPageHeader title={t('termsTitle')} />

          <div className="flex flex-col gap-2 border-t border-base-line-primary pt-6 max-pad:border-t-0">
            <h2 className="hidden text-xl font-bold leading-[30px] text-base-label pad:block">{t('allTerms')}</h2>

            {articles.map((article) => (
              <ArticleListItem key={article.id} title={article.title} date={article.date} href={article.href} />
            ))}

            <div className="flex justify-center">
              <Pagination currentPage={page} totalPages={totalPages} onChange={handlePageChange} />
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
